import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  findGetParameter,
  getData,
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import moment from "moment";
import "moment-timezone";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function EventJudgeReport() {
  let { eventId } = useParams();
  const reportId = findGetParameter("reportId");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [judgeReport, setJudgeReport] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        var apiEvent = await getData(`/events/${eventId}`, {
          queryStringParameters: {},
        });
        setEvent(apiEvent);

        if (!apiEvent.judgeReport) {
          var apiJudgeReport = await getData(`/events/${eventId}/judgeReport`, {
            queryStringParameters: {
              reportId: reportId,
            },
          });
          setJudgeReport(apiJudgeReport);
        } else {
          var submittedLeagues = Object.values(
            apiEvent.judgeReport?.submittedLeagues
          ).filter((report) => {
            return report.id === reportId;
          });
          if (submittedLeagues.length) {
            apiEvent.judgeReport.submittedLeague = submittedLeagues[0];
          }
          delete apiEvent.judgeReport.submittedLeagues;
          setJudgeReport(apiEvent.judgeReport);
        }

        setLoading(false);
      } catch (err) {
        postNotification("error", err);
        navigate(apiEvent ? `/event/${apiEvent.id}` : "/home", {
          replace: false,
        });
        return;
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      {!loading && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid container sx={{ gap: 2 }}>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
              }}
            >
              <Typography variant="h3">
                {judgeReport?.submittedLeague?.league?.name || "Judge Report"}
              </Typography>

              <Typography variant="h4">{event.name} - Judge Report</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="body1">Judge:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ fontWeight: "600" }}>
                    {`${judgeReport?.judgeFirstName} ${judgeReport?.judgeLastName}`.trim()}
                  </Typography>
                </Grid>

                {judgeReport?.submittedLeague && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body1">Submitted:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {moment(judgeReport.submittedLeague.submitted).format(
                          "HH:mm MMM Do, YYYY"
                        )}
                      </Typography>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Typography variant="body1" gutterBottom>
                    Report:
                  </Typography>
                  {judgeReport.text && judgeReport.text !== "" && (
                    <Typography variant="body1" gutterBottom paragraph>
                      {judgeReport.text.split("\n").map((i, key) => {
                        return <Box key={key}>{i}</Box>;
                      })}
                    </Typography>
                  )}
                </Grid>

                {judgeReport.imageFiles?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body1" gutterBottom>
                      Gallery
                    </Typography>
                    <Grid container spacing={1}>
                      {judgeReport.imageFiles.map((picture, rowIndex) => {
                        return (
                          <Grid item xs={12}>
                            <ImageBox
                              label="Image"
                              fileId={
                                judgeReport.imageFiles[rowIndex].photoFileId ||
                                null
                              }
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      )}
      {loading && <ModalLoadingFull />}
    </Box>
  );
}

const ImageBox = (props) => {
  const { label, defaultUrl, fileId } = props;
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  const fetchFile = async (fileId) => {
    if (!fileId) return;

    var returnObj;
    try {
      returnObj = await getData(`/files/${fileId}`, {
        queryStringParameters: {},
      });
    } catch (err) {
      console.error(err);
    }
    return returnObj;
  };

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      if (!fileId) {
        setImage(null);
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === fileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = await fetchFile(fileId);
        if (file) {
          setImage({
            id: fileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [fileId]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        maxHeight: "388px",
        p: 1,
        border: 1,
        borderColor: "divider",
      }}
    >
      <img
        src={
          image?.url ||
          defaultUrl ||
          `${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/images/EventImage${
            theme?.palette?.mode === "dark" ? "Dark" : ""
          }.png`
        }
        alt={label}
        style={{ maxWidth: "100%", maxHeight: "372px" }}
      />
      {loading && (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <CircularProgress size={100} />
        </Box>
      )}
    </Box>
  );
};
